import React, { useState, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useStartActivationJob } from './hooks/useActivationJob';
import ActivationJobPanel from './components/ActivationJobPanel';
import RecentActivationJobs from './components/RecentActivationJobs';
import { findDuplicateCodes, type ActivationPreviewRow } from './utils';

const ActivateBookPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ActivationPreviewRow[]>([]);
  const duplicateCodes = useMemo(() => findDuplicateCodes(previewData), [previewData]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  // Job nằm trên URL: rời trang rồi quay lại (hoặc gửi link) vẫn xem tiếp được
  const [searchParams, setSearchParams] = useSearchParams();
  const jobId = searchParams.get('job');

  const startMutation = useStartActivationJob();

  // Download example Excel file
  const handleDownloadExample = () => {
    // Create example data with proper column structure (A, B, C, D)
    const exampleData = [
      ['STT', 'Họ và tên', 'Số điện thoại: Tên đăng nhập', 'Mã kích hoạt'],
      [1, 'Nguyễn Văn A', '0123456789', 'BOOKS-TT2025-A1B2C3D45'],
      [2, 'Trần Thị B', '0987654321', 'BOOKS-TT2025-A1B2C3D46'],
      [3, 'Lê Văn C', 'user@example.com', 'BOOKS-TT2025-A1B2C3D47']
    ];

    const ws = XLSX.utils.aoa_to_sheet(exampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Kích hoạt sách');
    XLSX.writeFile(wb, 'mau_kich_hoat_sach.xlsx');
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      setErrorMessage('');
      processExcelFile(file);
    }
    // Reset input value to allow re-selecting the same file
    if (event.target) {
      event.target.value = '';
    }
  };

  // Process Excel file
  const processExcelFile = (file: File) => {
    setIsProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to array of arrays to access by column index
        const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
        
        // Bỏ dòng tiêu đề; giữ số dòng Excel để cảnh báo trùng mã khớp số dòng server báo lỗi
        const activateBooks: ActivationPreviewRow[] = rawData
          .map((row: any[], index: number) => ({ row, line: index + 1 }))
          .slice(1)
          .filter(({ row }) => row && row.length >= 4)
          .map(({ row, line }) => ({
            line,
            name: String(row[1] ?? '').trim(),
            userName: String(row[2] ?? '').trim(),
            code: String(row[3] ?? '').trim(),
          }));

        setPreviewData(activateBooks);
        // Không có dòng nào thì cũng không có nút xác nhận: phải nói lý do, kẻo admin tưởng trang bị treo
        if (activateBooks.length === 0) {
          setErrorMessage('File không có dòng dữ liệu nào. Vui lòng kiểm tra lại file (dữ liệu bắt đầu từ dòng 2).');
        }
        setIsProcessing(false);
      } catch (error) {
        console.error('Error processing Excel file:', error);
        setErrorMessage('Lỗi khi đọc file Excel. Vui lòng kiểm tra định dạng file.');
        setIsProcessing(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Server chỉ nhận file rồi kích hoạt nền, nên request trả ngay dù file vài nghìn dòng
  const handleUploadData = () => {
    if (!uploadedFile) return;

    startMutation.mutate(uploadedFile, {
      onSuccess: (job) => {
        setPreviewData([]);
        setUploadedFile(null);
        setErrorMessage('');
        setSearchParams({ job: job.id });
      },
      onError: (error: any) => {
        console.error('Start activation job failed:', error);
      },
    });
  };

  // Clear uploaded data
  const handleClearData = () => {
    setPreviewData([]);
    setUploadedFile(null);
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kích hoạt Sách</h1>
            <p className="text-gray-600">Tải lên file Excel chứa danh sách người dùng và mã kích hoạt</p>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Tải lên file Excel</h3>
          <button
            onClick={handleDownloadExample}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Download className="h-4 w-4 mr-2" />
            Tải mẫu Excel
          </button>
        </div>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <div className="text-center">
            <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <label htmlFor="file-upload" className="cursor-pointer">
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  Chọn file Excel để tải lên
                </span>
                <span className="mt-1 block text-sm text-gray-500">
                  File Excel cần có 4 cột: A (STT), B (Họ và tên), C (SĐT/Email), D (Mã kích hoạt)
                </span>
              </label>
              <input
                ref={fileInputRef}
                id="file-upload"
                name="file-upload"
                type="file"
                accept=".xlsx,.xls"
                className="sr-only"
                onChange={handleFileUpload}
              />
            </div>
            <div className="mt-6">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Upload className="h-4 w-4 mr-2" />
                Chọn file
              </button>
            </div>
          </div>
        </div>

        {/* File Info */}
        {uploadedFile && (
          <div className="mt-4 p-4 bg-blue-50 rounded-md">
            <div className="flex items-center">
              <FileSpreadsheet className="h-5 w-5 text-blue-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-800">
                  File đã chọn: {uploadedFile.name}
                </p>
                <p className="text-sm text-blue-600">
                  Kích thước: {(uploadedFile.size / 1024).toFixed(2)} KB
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Processing Status */}
        {isProcessing && (
          <div className="mt-4 p-4 bg-yellow-50 rounded-md">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-yellow-600"></div>
              <div className="ml-3">
                <p className="text-sm font-medium text-yellow-800">
                  Đang xử lý file Excel...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="mt-4 p-4 bg-red-50 rounded-md">
            <div className="flex items-center">
              <XCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">
                  {errorMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        {startMutation.isError && (
          <div className="mt-4 p-4 bg-red-50 rounded-md">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
              <p className="ml-3 text-sm font-medium text-red-800">
                {(startMutation.error as any)?.response?.data?.message || 'Lỗi khi tải lên dữ liệu. Vui lòng thử lại.'}
              </p>
            </div>
          </div>
        )}

        {jobId && <ActivationJobPanel jobId={jobId} />}
      </div>

      {!jobId && <RecentActivationJobs onOpen={(id) => setSearchParams({ job: id })} />}

      {/* Preview Data */}
      {previewData.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Xem trước dữ liệu ({previewData.length} bản ghi)
              </h3>
              <div className="flex space-x-3">
                <button
                  onClick={handleClearData}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Xóa dữ liệu
                </button>
                <button
                  onClick={handleUploadData}
                  disabled={startMutation.isPending || !uploadedFile}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {startMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 mr-2 border-t-2 border-b-2 border-white"></div>
                      Đang tải lên...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Xác nhận tải lên
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {duplicateCodes.size > 0 && (
            <div className="mx-4 mt-4 p-4 bg-yellow-50 rounded-md" role="alert">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
                <div className="ml-3 text-sm text-yellow-800">
                  <p className="font-medium">
                    {duplicateCodes.size} mã bị lặp trong file. Mỗi mã chỉ kích hoạt được cho một tài khoản, các dòng sau dùng lại mã cho tài khoản khác sẽ bị lỗi.
                  </p>
                  <ul className="mt-1 list-disc list-inside">
                    {Array.from(duplicateCodes).map(([code, lines]) => (
                      <li key={code}>
                        <code className="bg-yellow-100 px-1 rounded text-xs">{code}</code>: dòng {lines.join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    STT
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Họ và tên (Cột B)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SĐT/Email (Cột C)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mã kích hoạt (Cột D)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {previewData.map((item, index) => (
                  <tr key={index} className={duplicateCodes.has(item.code) ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.userName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                        {item.code}
                      </code>
                      {duplicateCodes.has(item.code) && (
                        <span className="ml-2 text-xs font-medium text-yellow-700">Trùng mã</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivateBookPage;
